import React from 'react';
import styled from 'styled-components';

import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import { Body12 } from 'kingpin/atoms/Typography/Body';

import { CardShell, SHELL_PADDING } from './CardChrome';
import { NO_DRAG_CLASS, STUDIO_ELEMENT_ID_ATTR } from './constants';
import { SetupStep } from './setupSteps';
import useElementSize from './useElementSize';
import { DEFAULT_VIS_TYPE, getVisType, isChartVisType } from './visTypes';
import SampleChart from './VisTypePreview/SampleChart';
import { SAMPLE_CARDS } from './VisTypePreview/SampleCards';

// The gap between the card and the banner beneath it
const BANNER_GAP = 8;

// Its top padding is the header's, when it has none. Left unpositioned, so
// the Hint centres on the whole card (the CardShell) rather than beneath its
// header.
const Body = styled.div<{ $isHeaderHidden: boolean; $isFlush: boolean }>`
  flex: 1;
  min-height: 0;
  padding: ${(props) =>
    props.$isFlush
      ? '0'
      : `${props.$isHeaderHidden ? SHELL_PADDING.y : 0}px ${SHELL_PADDING.x}px ${SHELL_PADDING.y}px`};
`;

// The sample, faded back so it reads as what the gadget will look like
// rather than as data
const Sample = styled.div`
  height: 100%;
  overflow: hidden;
  opacity: 0.3;
  filter: grayscale(0.4);
  pointer-events: none;
`;

const Hint = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${SHELL_PADDING.x}px;
  pointer-events: none;
`;

// While the block's hovered (or the Composite element it's in) it grows in
// place, inviting the click, and its edge takes a faint tint of the selection
// ring's colour, as a hint of what selecting does
const HintPill = styled.div<{ $isElement: boolean }>`
  max-width: 100%;
  padding: 3px 9px;
  border: 1px solid var(--color-border-tertiary);
  border-radius: 999px;
  background-color: var(--color-bg-primary);
  box-shadow:
    0 0 0 0 transparent,
    var(--shadow-dropdown);
  transform-origin: center;
  transition:
    transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1),
    border-color 220ms ease-out,
    box-shadow 220ms ease-out;

  ${(props) =>
    props.$isElement ? `[${STUDIO_ELEMENT_ID_ATTR}]` : CardShell}:hover
  & {
    transform: scale(1.06);
    border-color: var(--color-studio-selection-subtle);
    box-shadow:
      0 0 0 2px
        color-mix(in srgb, var(--color-studio-selection) 15%, transparent),
      var(--shadow-md);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    ${(props) =>
      props.$isElement ? `[${STUDIO_ELEMENT_ID_ATTR}]` : CardShell}:hover
    & {
      transform: none;
    }
  }
`;

// Holds the card and, while it's selected, the banner beneath it. Its own
// stacking context, so the banner stays within the card's layer.
export const SetupFrame = styled.div`
  position: relative;
  height: 100%;
  isolation: isolate;
`;

// Floats just below the selected card, as wide as it, apart from its
// selection ring and edged like an info badge
const Banner = styled.div`
  @keyframes studio-setup-banner-in {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
  }

  position: absolute;
  top: calc(100% + ${BANNER_GAP}px);
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border: 1px solid var(--color-badge-info-border);
  border-radius: 12px;
  background-color: var(--color-badge-info-bg);
  box-shadow: var(--shadow-dropdown);
  cursor: default;
  animation: studio-setup-banner-in 180ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const BANNER_ICON_SIZE = 16;
const BANNER_ICON_GAP = 6;

// The info icon and title, centred on each other. The title's set closer than
// Typography's line height, for when it wraps on a narrow block.
const BannerTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${BANNER_ICON_GAP}px;
  min-width: 0;

  > :last-child {
    line-height: 17px;
  }
`;

// Beneath the title, lined up with it rather than the icon
const Steps = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding-left: ${BANNER_ICON_SIZE + BANNER_ICON_GAP}px;
`;

// A step's label is set closer than Typography's line height, as the title
// is, so one that wraps on a narrow block still reads as one step
const STEP_LINE_HEIGHT = 16;

// Its tick or ring sits on the label's first line, should it wrap
const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;

  ${Body12} {
    line-height: ${STEP_LINE_HEIGHT}px;
  }
`;

// Holds a step's tick or ring, centred on the label's first line
const StepMarker = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: ${STEP_LINE_HEIGHT}px;
`;

// An unticked step's box: a faint dotted ring, still to be filled in
const StepRing = styled.div`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  margin: 0 2px;
  border: 1.5px dotted var(--color-icon-tertiary);
  border-radius: 50%;
`;

// An unticked step's label, looking as it would unclicked, that opens its
// settings section
const StepButton = styled.button`
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    border-radius: 2px;
  }
`;

// A chart block that can't draw yet: a faded sample of its type, with a pill
// asking for it to be selected. Selected, the pill gives way to its
// SetupBanner. Where the block can't be selected (fullscreen, a preview), the
// sample's shown alone.
const SetupPlaceholder = ({
  visType = DEFAULT_VIS_TYPE,
  isHintShown,
  isHeaderHidden = false,
  isElement = false,
  isFlush = false,
  testId,
}: {
  visType?: Studio.VisType;
  isHintShown: boolean;
  isHeaderHidden?: boolean;
  // A Composite element's, which grows its hint as the element's hovered,
  // not the whole block
  isElement?: boolean;
  // Unpadded, in something that pads it already (a Number element's cell)
  isFlush?: boolean;
  testId?: string;
}) => {
  const SampleCard = SAMPLE_CARDS[visType];
  const sample = useElementSize<HTMLDivElement>();

  return (
    <Body
      $isHeaderHidden={isHeaderHidden}
      $isFlush={isFlush}
      data-testid={testId}
    >
      <Sample ref={sample.ref} data-testid={`studio-placeholder-${visType}`}>
        {SampleCard ? (
          <SampleCard />
        ) : (
          // Drawn once it's been measured (never, in jsdom)
          sample.width > 0 &&
          sample.height > 0 && (
            <SampleChart
              visType={visType}
              width={sample.width}
              height={sample.height}
              hasEntrance={false}
            />
          )
        )}
      </Sample>
      {isHintShown && (
        <Hint>
          <HintPill $isElement={isElement}>
            <Typography.Body type="Body 12" isEllipsis>
              Select To Set Up
            </Typography.Body>
          </HintPill>
        </Hint>
      )}
    </Body>
  );
};

// What a selected block still needs (see getSetupSteps), each ticked off as
// it's set up in its settings, where clicking a step still to do takes you
// (onStepClick), once its section's shown. Placed after the card, in a
// SetupFrame.
export const SetupBanner = ({
  visType = DEFAULT_VIS_TYPE,
  steps,
  onStepClick,
}: {
  visType?: Studio.VisType;
  steps: SetupStep[];
  // Absent where the block's view-only (fullscreen)
  onStepClick?: (section: SetupStep['id']) => void;
}) => {
  const { label } = getVisType(visType);
  // A Spider Chart, but a Number
  const name = isChartVisType(visType) ? `${label} Chart` : label;
  // Dimensions are hidden until there's a metric (see DimensionsSection), so
  // can't be revealed before then
  const isMetricNeeded = steps.some((s) => s.id === 'metrics' && !s.isDone);
  const isLinked = (step: SetupStep): boolean =>
    !step.isDone && !(step.id === 'dimensions' && isMetricNeeded);

  return (
    <Banner className={NO_DRAG_CLASS} data-testid="studio-setup-banner">
      <BannerTitle>
        <Icon
          icon="info"
          color="var(--color-info-v2)"
          width={BANNER_ICON_SIZE}
          height={BANNER_ICON_SIZE}
        />
        <Typography.Body type="Body 13 Bold">
          {`To show this ${name}:`}
        </Typography.Body>
      </BannerTitle>
      <Steps>
        {steps.map((step) => (
          <Step
            key={step.id}
            data-testid={`studio-setup-step-${step.id}${
              step.isDone ? '-done' : ''
            }`}
          >
            <StepMarker>
              {step.isDone ? (
                <Icon
                  icon="checkmark"
                  color="var(--color-success-v2)"
                  width={14}
                  height={14}
                />
              ) : (
                <StepRing />
              )}
            </StepMarker>
            {onStepClick && isLinked(step) ? (
              <StepButton
                type="button"
                onClick={() => onStepClick(step.id)}
                data-testid={`studio-setup-step-${step.id}-link`}
              >
                <Typography.Body
                  type="Body 12"
                  color="var(--color-text-primary)"
                >
                  {step.label}
                </Typography.Body>
              </StepButton>
            ) : (
              <Typography.Body
                type="Body 12"
                color={
                  step.isDone
                    ? 'var(--color-text-tertiary)'
                    : 'var(--color-text-primary)'
                }
              >
                {step.label}
              </Typography.Body>
            )}
          </Step>
        ))}
      </Steps>
    </Banner>
  );
};

export default SetupPlaceholder;
